<template>
  <!-- 项目管理（开发管理顶层单元） -->
  <div class="project-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="项目名称/编号/提出人" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.projectType" placeholder="全部类型" clearable>
            <template #prefix>项目类型</template>
            <el-option v-for="t in projectTypeOptions" :key="t" :label="t" :value="t"/>
          </el-select>
          <el-select v-model="searchInfo.priority" placeholder="全部优先级" clearable>
            <template #prefix>优先级</template>
            <el-option v-for="p in priorityOptions" :key="p" :label="p" :value="p"/>
          </el-select>
          <el-select v-model="searchInfo.projectStatus" placeholder="全部状态" clearable>
            <template #prefix>状态</template>
            <el-option v-for="s in projectStatusOptions" :key="s.value" :label="s.label" :value="s.value"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog()">+ 新建项目</page-button>
        </template>
      </search-condition>
      <list-table>
        <yq-table :data="dataList" height="100%" stripe border page-name="projectList">
          <yq-table-column label="项目编号" prop="projectCode" width="150rem">
            <template v-slot="{row}">
              <span class="code-text">{{ row.projectCode || '—' }}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="项目名称" prop="projectName" min-width="180rem">
            <template v-slot="{row}">
              <strong>{{ row.projectName || '—' }}</strong>
              <div v-if="row.description" class="muted ellipsis">{{ row.description }}</div>
            </template>
          </yq-table-column>
          <yq-table-column label="提出人" prop="sourceName" width="110rem">
            <template v-slot="{row}">
              {{ row.sourceName || '—' }}
            </template>
          </yq-table-column>
          <yq-table-column label="类型" prop="projectType" width="110rem" align="center">
            <template v-slot="{row}">
              <el-tag v-if="row.projectType" size="small" :type="projectTypeTag(row.projectType)">{{ row.projectType }}</el-tag>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="优先级" prop="priority" width="100rem" align="center">
            <template v-slot="{row}">
              <el-tag v-if="row.priority" size="small" effect="dark" :type="priorityTag(row.priority)">{{ row.priority }}</el-tag>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="期望完成" prop="expectDate" width="115rem" align="center">
            <template v-slot="{row}">
              {{ dateOnly(row.expectDate) }}
            </template>
          </yq-table-column>
          <yq-table-column label="任务/批次" prop="taskCount" width="120rem" align="center">
            <template v-slot="{row}">
              <span class="muted">任务</span> {{ row.taskCount || 0 }}
              <span class="muted" style="margin-left:8px">批次</span> {{ row.sprintCount || 0 }}
            </template>
          </yq-table-column>
          <yq-table-column label="状态" prop="projectStatus" width="100rem" align="center">
            <template v-slot="{row}">
              <el-tag v-if="hasStatus(row.projectStatus)" size="small" :type="statusType(row.projectStatus)">{{ statusText(row.projectStatus) }}</el-tag>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="300rem" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="openDetail(row)">详情</table-button>
              <table-button @click="openTaskBoard(row)">任务看板</table-button>
              <table-button @click="openDialog(row)">编辑</table-button>
              <table-button @click="openStatusDialog(row)">状态流转</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新建/编辑项目弹窗 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑项目' : '新建项目'" dialog-width="2" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="2" label-width="2.4rem" icon-width="0" ref="dialogForm">
        <form-item label="项目名称" :required="true" name="projectName" error-message="请填写项目名称" :show-block="true">
          <el-input v-model="dialogData.projectName" placeholder="一句话说明要交付的内容"/>
        </form-item>
        <form-item label="项目描述" :show-block="true">
          <el-input v-model="dialogData.description" type="textarea" :rows="2" placeholder="项目描述 / 背景说明"/>
        </form-item>
        <form-item label="提出人">
          <employee-select v-model="dialogData.sourceId" placeholder="默认当前登录人" @change="onSourceChange"/>
        </form-item>
        <form-item label="期望完成">
          <el-date-picker v-model="dialogData.expectDate" type="date" value-format="YYYY-MM-DD" placeholder="期望完成时间" style="width: 100%"/>
        </form-item>
        <form-item label="项目类型">
          <el-select v-model="dialogData.projectType" placeholder="请选择" clearable style="width: 100%">
            <el-option v-for="t in projectTypeOptions" :key="t" :label="t" :value="t"/>
          </el-select>
        </form-item>
        <form-item label="优先级">
          <el-select v-model="dialogData.priority" placeholder="请选择" clearable style="width: 100%">
            <el-option v-for="p in priorityOptions" :key="p" :label="p" :value="p"/>
          </el-select>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="备注信息"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveData">保存</page-button>
      </template>
    </el-dialog>

    <!-- 状态流转弹窗 -->
    <el-dialog v-model="showStatusDialog" title="项目状态流转" dialog-width="1" :close-on-click-modal="false">
      <div class="status-picker-wrap">
        <el-select v-model="statusTarget">
          <el-option v-for="s in projectStatusOptions" :key="s.value" :value="s.value" :disabled="statusRow && s.value === statusRow.projectStatus" :label="s.label"/>
        </el-select>
      </div>
      <template #footer>
        <page-button @click="showStatusDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveStatus">确认流转</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import projectApi from "@/api/dz-project/project/projectApi";
import {PROJECT_STATUS_OPTIONS, projectStatusLabel, projectStatusTag} from "./projectStatus";

// 项目类型：新功能/优化/缺陷修复/技术改造
const PROJECT_TYPE_OPTIONS = ["新功能", "优化", "缺陷修复", "技术改造"];
// 优先级：紧急/高/中/低
const PRIORITY_OPTIONS = ["紧急", "高", "中", "低"];
// 项目状态六态采用 int 存储，中文映射见 ./projectStatus.js

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "ProjectList",
  components: {FormItems, FormItem, EmployeeSelect},
  data() {
    return {
      searchInfo: {pageNumber: 1},
      dataList: [],
      showDialog: false,
      dialogData: {},
      showStatusDialog: false,
      statusRow: null,
      statusTarget: null,
      // 静态枚举（不建字典表；项目/任务状态 int 存储，展示转中文）
      projectTypeOptions: PROJECT_TYPE_OPTIONS,
      priorityOptions: PRIORITY_OPTIONS,
      projectStatusOptions: PROJECT_STATUS_OPTIONS,
    };
  },
  beforeRouteLeave(to, from, next) {
    // 将当前页面数据进行缓存
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.resetSearch();
  },
  methods: {
    async queryList() {
      // 空字符串/空值不提交，避免空串绑定后端 Integer（如 projectStatus 清空）报错
      const params = {};
      Object.keys(this.searchInfo).forEach(k => {
        const v = this.searchInfo[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      const {records, totalCount} = await projectApi.queryList(params);
      this.dataList = records || [];
      this.searchInfo.totalCount = totalCount;
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    projectTypeTag(type) {
      const map = {"新功能": "primary", "优化": "success", "缺陷修复": "danger", "技术改造": "warning"};
      return map[type] || "info";
    },
    priorityTag(priority) {
      const map = {"紧急": "danger", "高": "warning", "中": "primary", "低": "info"};
      return map[priority] || "info";
    },
    statusText(status) {
      return projectStatusLabel(status);
    },
    statusType(status) {
      return projectStatusTag(status);
    },
    hasStatus(status) {
      return status !== null && status !== undefined && status !== "";
    },
    dateOnly(v) {
      return v ? String(v).slice(0, 10) : "—";
    },
    toDateValue(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    onSourceChange(data) {
      // EmployeeSelect change 事件携带选中的员工对象
      this.dialogData.sourceName = data ? data.employeeName : "";
    },
    openTaskBoard(row) {
      // 带项目过滤跳转任务看板
      this.router.push({path: "/project/dev/task", query: {projectId: row.id}});
    },
    openDetail(row) {
      // 跳转项目详情页
      this.router.push({path: "/project/dev/info/detail", query: {id: row.id}});
    },
    openDialog(row) {
      if (row) {
        this.dialogData = JSON.parse(JSON.stringify(row));
        this.dialogData.expectDate = this.toDateValue(this.dialogData.expectDate);
      } else {
        this.dialogData = {projectName: "", description: "", sourceId: "", sourceName: "", projectType: "", priority: "", expectDate: "", remark: ""};
      }
      this.showDialog = true;
    },
    async saveData() {
      if (!this.$refs.dialogForm.validCheck()) { // 数据验证不通过
        return;
      }
      const payload = JSON.parse(JSON.stringify(this.dialogData));
      payload.expectDate = payload.expectDate || null; // 空日期置空，避免传空串
      await projectApi.save(payload);
      this.showDialog = false;
      this.resetSearch();
    },
    openStatusDialog(row) {
      this.statusRow = row;
      // 默认选中下一个未流转状态（int 状态码）
      const idx = PROJECT_STATUS_OPTIONS.findIndex(o => o.value === row.projectStatus);
      const next = PROJECT_STATUS_OPTIONS[(idx + 1) % PROJECT_STATUS_OPTIONS.length];
      this.statusTarget = next ? next.value : null;
      this.showStatusDialog = true;
    },
    async saveStatus() {
      if (!this.statusRow) {
        return;
      }
      if (this.statusTarget === null || this.statusTarget === this.statusRow.projectStatus) {
        this.$message.warning("请选择与当前不同的状态");
        return;
      }
      await projectApi.updateStatus({id: this.statusRow.id, status: this.statusTarget});
      this.showStatusDialog = false;
      this.resetSearch();
    },
  },
};
</script>

<style lang="scss" scoped>
.project-list {
  height: 100%;
  .code-text {
    font-family: Consolas, Menlo, monospace;
    font-size: 12px;
    color: var(--yq-font-color-desc);
  }
  .ellipsis {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
.status-picker-wrap {
  padding: 4px 0 8px;
}
</style>
